import {
  CreateLetterheadMutation,
  CreateLetterheadMutationVariables,
  DeleteLetterheadMutation,
  DeleteLetterheadMutationVariables,
  DuplicateLetterheadMutation,
  DuplicateLetterheadMutationVariables,
  GetLetterheadByUuidQuery,
  GetLetterheadByUuidQueryVariables,
  GetLetterheadQuery,
  GetLetterheadQueryVariables,
  GetLetterheadsQuery,
  GetLetterheadsQueryVariables,
  UpdateLetterheadMutation,
  UpdateLetterheadMutationVariables,
} from 'types/graphql'

import { useActiveOrgId } from '../hooks/use-active-org-id'
import { QueryKey, useMutation, useQuery } from '../utils/queryClient'

const GetLetterheads = gql`
  query GetLetterheadsQuery($organizationId: Int!) {
    letterheads(organizationId: $organizationId) {
      ...LetterheadListFragment
    }
  }
  fragment LetterheadListFragment on Letterhead {
    id
    uuid
    backgroundColor
    centerFile {
      ...FileFragment
    }
    centerFileWidthPx
    centerText
    defaultLetterheadRecordSettings {
      configConnection {
        recordType {
          id
          displayName
        }
      }
    }
    isDefaultForOrganization
    leftText
    name
    organization {
      id
    }
    rightText
  }
`

export const useLetterheadsQuery = () => {
  const organizationId = useActiveOrgId()
  return useQuery<GetLetterheadsQuery, GetLetterheadsQueryVariables>({
    queryKey: [QueryKey.Letterheads, organizationId],
    queryDocument: GetLetterheads,
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
}

const GetLetterhead = gql`
  query GetLetterheadQuery($id: Int!) {
    letterhead(id: $id) {
      ...LetterheadDetailFragment
    }
  }
`

export const useLetterheadQuery = (id: number, enabled: boolean) => {
  return useQuery<GetLetterheadQuery, GetLetterheadQueryVariables>({
    queryKey: [QueryKey.Letterhead, id],
    queryDocument: GetLetterhead,
    variables: { id },
    enabled,
  })
}

const Create = gql`
  mutation CreateLetterheadMutation($input: CreateLetterheadInput!) {
    createLetterhead(input: $input) {
      id
      uuid
    }
  }
`

export const useCreateLetterheadMutation = () => {
  return useMutation<CreateLetterheadMutation, CreateLetterheadMutationVariables>({
    mutationDocument: Create,
  })
}

const Update = gql`
  mutation UpdateLetterheadMutation($id: Int!, $input: CreateLetterheadInput!) {
    updateLetterhead(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateLetterheadMutation = () => {
  return useMutation<UpdateLetterheadMutation, UpdateLetterheadMutationVariables>({
    mutationDocument: Update,
  })
}

const Delete = gql`
  mutation DeleteLetterheadMutation($id: Int!) {
    deleteLetterhead(id: $id)
  }
`

export const useDeleteLetterheadMutation = () => {
  return useMutation<DeleteLetterheadMutation, DeleteLetterheadMutationVariables>({
    mutationDocument: Delete,
  })
}

const GetLetterheadByUuid = gql`
  query GetLetterheadByUuidQuery($uuid: String!) {
    getLetterheadByUuid(uuid: $uuid) {
      ...LetterheadDetailFragment
    }
  }
`

export const useGetLetterheadByUuidQuery = (uuid: string | undefined) => {
  return useQuery<GetLetterheadByUuidQuery, GetLetterheadByUuidQueryVariables>({
    queryKey: [QueryKey.Letterhead, uuid],
    queryDocument: GetLetterheadByUuid,
    variables: { uuid: uuid ?? '' },
    enabled: !!uuid,
  })
}

const Duplicate = gql`
  mutation DuplicateLetterheadMutation($id: Int!) {
    duplicateLetterhead(id: $id) {
      id
      uuid
    }
  }
`

export const useDuplicateLetterheadMutation = () => {
  return useMutation<DuplicateLetterheadMutation, DuplicateLetterheadMutationVariables>({
    mutationDocument: Duplicate,
  })
}
